import * as React from 'react';
import { useState, useCallback, useEffect } from 'react';
import { BedrockAgentRuntimeClient, InvokeAgentCommand } from '@aws-sdk/client-bedrock-agent-runtime';
import { SSMClient, GetParameterCommand } from '@aws-sdk/client-ssm';
import '../Chat.css';
import { marked } from 'marked';
import { getSession } from '@/utils/helpers';
import logger from '@/utils/logger';
import { getUserErrorMessage } from './processBedrockError';
import MessageBox, { Message, OnReferenceClick } from '../ChatComponents/MessageBox';
import ChatInputBox from '../ChatComponents/ChatInputBox';

type Props = {
  onReferenceClick: OnReferenceClick;
};

const ChatWindow = ({ onReferenceClick }: Props) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string>(generateSessionId());
  const [bedrockClient, setBedrockClient] = useState<BedrockAgentRuntimeClient | null>(null);
  const [agentID, setAgentID] = useState<string>('');
  const [agentAliasID, setAgentAliasID] = useState<string>('');
  const [selectedCitation, setSelectedCitation] = useState<{ text: string; url: string } | null>(null);

  const handleSidebarClose = () => setSelectedCitation(null);
  {
    selectedCitation && <CitationSidebar citation={selectedCitation} onClose={handleSidebarClose} />;
  }

  // Initialize clients and parameters on mount
  useEffect(() => {
    initializeBedrockClient();
    fetchAgentParameters();
  }, []);

  // Initialize Bedrock client
  const initializeBedrockClient = async () => {
    try {
      const session = await getSession();
      const credentials = session?.credentials;
      if (credentials) {
        const client = new BedrockAgentRuntimeClient({
          region: 'eu-central-1',
          credentials: {
            accessKeyId: credentials.accessKeyId,
            secretAccessKey: credentials.secretAccessKey,
            sessionToken: credentials.sessionToken,
          },
        });
        setBedrockClient(client);
        logger.log('Bedrock client initialized.');
      } else {
        console.error('No AWS credentials available.');
      }
    } catch (error) {
      console.error('Error initializing Bedrock client:', error);
    }
  };

  // Fetch parameters for agent
  const fetchAgentParameters = async () => {
    try {
      const session = await getSession();
      const credentials = session?.credentials;
      const bedrockAgentIDKey = import.meta.env.VITE_BEDROCK_AGENT_ID_KEY;
      const bedrockAgentAliasIDKey = import.meta.env.VITE_BEDROCK_AGENT_ALIAS_ID_KEY;

      if (!bedrockAgentIDKey || !bedrockAgentAliasIDKey) {
        throw new Error(
          `Missing environment variables for agent ID or alias ID. BedrockAgentIDKey: ${bedrockAgentIDKey}, BedrockAgentAliasIDKey: ${bedrockAgentAliasIDKey}`
        );
      }

      if (credentials) {
        const client = new SSMClient({
          region: 'eu-central-1',
          credentials: {
            accessKeyId: credentials.accessKeyId,
            secretAccessKey: credentials.secretAccessKey,
            sessionToken: credentials.sessionToken,
          },
        });

        const fetchParameter = async (name: string) => {
          const result = await client.send(new GetParameterCommand({ Name: name, WithDecryption: true }));
          return result.Parameter?.Value || '';
        };

        setAgentID(await fetchParameter(bedrockAgentIDKey));
        setAgentAliasID(await fetchParameter(bedrockAgentAliasIDKey));
      } else {
        throw new Error('No AWS credentials available.');
      }
    } catch (error) {
      console.error('Error fetching parameters:', error);
    }
  };

  const handleSubmit = (input: string, isRetrying: boolean = false) => {
    if (!input) return;

    const messageId = messages.length + 1;

    const newMessage: Message = { id: messageId.toString(), content: input, role: 'user' }; // Explicit type
    !isRetrying && setMessages((prev) => [...prev, newMessage]);
    getBotResponse(newMessage.content, messageId);
  };

  const clearConversation = () => {
    setMessages([]);
    setSessionId(generateSessionId());
  };

  const getBotResponse = useCallback(
    async (userMessage: string, lastMessageId: number) => {
      if (!bedrockClient || !agentID || !agentAliasID) {
        console.error('Bedrock client or parameters not initialized.');
        return;
      }

      const botMessageId = lastMessageId + 1;

      const tryFetch = async (): Promise<{
        success: boolean;
        fullResponse?: string;
        references?: { text: string; url: string }[];
        error?: unknown;
      }> => {
        try {
          const response = await bedrockClient.send(
            new InvokeAgentCommand({
              agentId: agentID,
              agentAliasId: agentAliasID,
              sessionId,
              inputText: userMessage,
              enableTrace: true,
            })
          );

          let fullResponse = '';
          const messageReferences: { text: string; url: string }[] = [];

          if (response.completion) {
            for await (const event of response.completion) {
              const chunk = event.chunk;
              const trace = event.trace;

              if (chunk) {
                fullResponse += new TextDecoder('utf-8').decode(chunk.bytes);
              } else if (trace) {
                const orchestrationTrace = trace?.trace?.orchestrationTrace;
                if (orchestrationTrace) {
                  const observation = orchestrationTrace?.observation || {};
                  const knowledgeBaseLookup = observation?.knowledgeBaseLookupOutput || {};
                  const retrievedReferences = knowledgeBaseLookup?.retrievedReferences || [];

                  retrievedReferences.forEach((ref) => {
                    const contentText = ref.content?.text;
                    const url = ref.location?.s3Location?.uri || ref.location?.webLocation?.url;
                    if (contentText && url) {
                      messageReferences.push({ text: contentText, url });
                    }
                  });
                }
              }
            }
          }

          return { success: true, fullResponse, references: messageReferences };
        } catch (error) {
          return { success: false, error };
        }
      };

      try {
        setIsLoading(true);

        // Add placeholder "thinking" bot message

        const firstAttempt = await tryFetch();

        if (firstAttempt.success) {
          setMessages((prev) => {
            const lastMsgIsError = prev[prev.length - 1]?.error;
            const newMessage: Message = { id: botMessageId.toString(), content: '', role: 'bot' };

            lastMsgIsError ? (prev[prev.length - 1] = newMessage) : prev.push(newMessage);

            return [...prev];
          });
          displayGradualResponse(botMessageId, firstAttempt.fullResponse!, firstAttempt.references!);
          return;
        }

        logger.warn('First attempt to get bot response failed, retrying...', firstAttempt.error);

        // Retry once
        const retryAttempt = await tryFetch();

        if (retryAttempt.success) {
          displayGradualResponse(botMessageId, retryAttempt.fullResponse!, retryAttempt.references!);
        } else {
          const userErrorMessage = getUserErrorMessage(retryAttempt.error);
          logger.error('Retry also failed:', retryAttempt.error);

          // Remove empty bot message and replace with error
          setMessages((prev) => {
            const hasErrorInLastEntry = !!prev[prev.length - 1]?.error;
            const botErrorMessage = {
              id: botMessageId.toString(),
              content: userErrorMessage,
              role: 'bot',
              error: userErrorMessage,
            } satisfies Message;

            //Overwrite last message if it was already an error, otherwise push new error message
            hasErrorInLastEntry ? (prev[prev.length - 1] = botErrorMessage) : prev.push(botErrorMessage);

            return [...prev];
          });
        }
      } catch {
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    },
    [bedrockClient, agentID, agentAliasID, sessionId]
  );

  const displayGradualResponse = (
    messageId: number,
    fullResponse: string,
    references: { text: string; url: string }[]
  ) => {
    const words = fullResponse.split(' ');
    let index = 0;

    const interval = setInterval(async () => {
      const htmlContent = await marked.parse(words.slice(0, index + 1).join(' '));
      setMessages((prev) =>
        prev.map((msg) => (msg.id === messageId.toString() ? { ...msg, content: htmlContent } : msg))
      );

      if (++index >= words.length) {
        clearInterval(interval);

        // After full response is revealed, add references
        setMessages((prev) => prev.map((msg) => (msg.id === messageId.toString() ? { ...msg, references } : msg)));
      }
    }, 15);
  };

  return (
    <div className="flex flex-col gap-4 w-full max-w-9xl mx-auto flex-1 grow overflow-hidden">
      <MessageBox
        messages={messages}
        onReferenceClick={onReferenceClick}
        messageLoading={isLoading}
        onSendMessage={handleSubmit}
      />

      <ChatInputBox onSendMessage={handleSubmit} isLoading={isLoading} clearConversation={clearConversation} />
      {selectedCitation && <CitationSidebar citation={selectedCitation} onClose={handleSidebarClose} />}
    </div>
  );
};

const CitationSidebar: React.FC<{
  citation: { text: string; url: string };
  onClose: () => void;
}> = ({ citation, onClose }) => (
  <div className="citation-sidebar">
    <button className="close-button" onClick={onClose}>
      Close
    </button>
    <p>
      <strong>Citation:</strong>
    </p>
    <p>{citation.text}</p>
    <a href={citation.url} target="_blank" rel="noopener noreferrer">
      {citation.url}
    </a>
  </div>
);

const generateSessionId = () => Math.random().toString(36).substring(2, 15);

export default ChatWindow;
